import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ClipboardCheck, Plus, LockKeyhole, X, Check } from "lucide-react";

export default function InventoryCountsPage() {
  const { branchId, hasPerm } = useAuth();
  const [counts, setCounts] = useState([]);
  const [active, setActive] = useState(null);
  const [items, setItems] = useState([]);
  const [balances, setBalances] = useState([]);
  const [busy, setBusy] = useState(false);
  const [countInput, setCountInput] = useState({}); // item_id -> counted_base

  const load = async () => {
    if (!branchId) return;
    try {
      const [c, i, b] = await Promise.all([
        api.get("/inventory/counts", { params: { branch_id: branchId, limit: 20 } }),
        items.length ? Promise.resolve({ data: items }) : api.get("/inventory/items"),
        api.get("/inventory/balances", { params: { branch_id: branchId } }),
      ]);
      setCounts(c.data);
      if (!items.length) setItems(i.data);
      setBalances(b.data);
      const draft = c.data.find((x) => x.status === "draft");
      if (draft && (!active || active.id !== draft.id)) {
        setActive(draft);
        const map = {};
        for (const l of (draft.lines || [])) map[l.item_id] = l.counted_base;
        setCountInput(map);
      } else if (!draft) {
        setActive(null); setCountInput({});
      }
    } catch (_) {}
  };
  useEffect(() => { load(); }, [branchId]);

  const openCount = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/inventory/counts?branch_id=${branchId}`);
      toast.success("Blind count opened");
      setActive(data); setCountInput({}); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveLine = async (item_id, counted, reason = "") => {
    if (counted === "" || counted === null || counted === undefined) return;
    try {
      const { data } = await api.post(`/inventory/counts/${active.id}/lines`, {
        item_id, counted_base: parseFloat(counted), reason,
      });
      setActive((prev) => {
        const lines = (prev.lines || []).filter((l) => l.item_id !== item_id);
        return { ...prev, lines: [...lines, data] };
      });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const finalize = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const res = await api.post(`/inventory/counts/${active.id}/finalize`, { notes: "" });
      toast.success(`Count finalised · variance value ${peso(res.data.total_variance_value)}`);
      setActive(null); setCountInput({}); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const linesByItem = useMemo(() => {
    const m = {};
    for (const l of (active?.lines || [])) m[l.item_id] = l;
    return m;
  }, [active]);

  const totalCounted = Object.keys(countInput).length;
  const totalVarianceQty = (active?.lines || []).reduce((s, l) => s + Math.abs(l.variance_base || 0), 0);

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="inventory-counts-page">
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">Inventory · Blind count</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">Stock counts</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            Count without peeking at the expected values. Variance rows generate <span className="mono ink">count_correction</span> movements on finalise — the ledger reconciles itself.
          </p>
        </div>
        <div className="text-right">
          <div className="section-eyebrow">{active ? "Draft count" : "Status"}</div>
          <div className="mt-3 display-heading text-4xl ink">
            {active ? `${totalCounted} of ${items.length}` : counts[0]?.status === "finalized" ? "Ready" : "None open"}
          </div>
          {active && <div className="mt-1 tiny-label ink-subtle">items counted so far</div>}
        </div>
      </div>

      {!branchId && (
        <div className="mt-14 border hairline-soft rounded-md p-8 text-center ink-subtle text-sm">
          Select a branch first.
        </div>
      )}

      {branchId && !active && (
        <>
          <div className="mt-10 animate-fade-up animate-fade-up-1">
            {hasPerm("inventory.adjust") && (
              <button onClick={openCount} disabled={busy} data-testid="inv-count-open" className="btn-pill btn-pill-solid h-11 disabled:opacity-50">
                <Plus size={14}/> Open a new blind count
              </button>
            )}
          </div>

          {counts.length > 0 && (
            <div className="mt-12 animate-fade-up animate-fade-up-2">
              <div className="section-eyebrow mb-4">Recent counts</div>
              <div className="divide-y hairline-soft border-y hairline-soft" data-testid="inv-count-history">
                {counts.filter((c) => c.status === "finalized").slice(0, 8).map((c) => (
                  <div key={c.id} className="py-3 grid grid-cols-12 items-center gap-3">
                    <div className="col-span-3 text-[11px] uppercase tracking-[0.24em] font-semibold ink">
                      {new Date(c.started_at).toLocaleDateString("en-PH", { day: "2-digit", month: "short", year: "2-digit" })}
                    </div>
                    <div className="col-span-2 tiny-label">{(c.lines || []).length} lines</div>
                    <div className="col-span-3 mono ink-muted text-sm">Var qty {c.total_variance_qty ?? 0}</div>
                    <div className={`col-span-2 mono text-sm font-semibold ${(c.total_variance_value || 0) > 100 ? "text-red-500" : "ink"}`}>
                      {peso(c.total_variance_value || 0)}
                    </div>
                    <div className="col-span-2 text-xs ink-subtle italic truncate">{c.notes || "—"}</div>
                  </div>
                ))}
                {counts.filter((c) => c.status === "finalized").length === 0 && (
                  <div className="py-6 ink-subtle text-center text-sm">No finalised counts yet.</div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {branchId && active && (
        <>
          <div className="mt-10 flex items-center justify-between animate-fade-up animate-fade-up-1">
            <div className="tiny-label ink-subtle">Started {new Date(active.started_at).toLocaleString("en-PH", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}</div>
            <div className="flex items-center gap-3">
              <div className="tiny-label">Variance so far · <span className="mono ink">{totalVarianceQty.toFixed(2)}</span></div>
              <button onClick={finalize} disabled={busy || (active.lines || []).length === 0}
                      data-testid="inv-count-finalize"
                      className="btn-pill btn-pill-solid h-10 disabled:opacity-50">
                <LockKeyhole size={13}/> Finalise count
              </button>
            </div>
          </div>

          <div className="mt-8 border-y hairline-soft divide-y hairline-soft animate-fade-up animate-fade-up-2" data-testid="inv-count-lines">
            <div className="grid grid-cols-12 py-3 tiny-label ink-subtle">
              <div className="col-span-4">Item</div>
              <div className="col-span-2 text-right">Counted</div>
              <div className="col-span-2 text-right">Recorded on save</div>
              <div className="col-span-2 text-right">Variance</div>
              <div className="col-span-2">Reason</div>
            </div>
            {items.filter((i) => i.active).map((i) => {
              const line = linesByItem[i.id];
              const balRow = balances.find((b) => b.item_id === i.id);
              return (
                <div key={i.id} className="grid grid-cols-12 py-3 items-center gap-2" data-testid={`inv-count-row-${i.id}`}>
                  <div className="col-span-4 pr-2">
                    <div className="text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{i.name}</div>
                    <div className="text-xs ink-subtle mono">{i.code} · {i.base_unit}</div>
                  </div>
                  <div className="col-span-2 text-right">
                    <input
                      type="number" step="0.01"
                      placeholder="—"
                      value={countInput[i.id] ?? ""}
                      onChange={(e) => setCountInput({ ...countInput, [i.id]: e.target.value })}
                      onBlur={(e) => e.target.value !== "" && saveLine(i.id, e.target.value)}
                      data-testid={`inv-count-input-${i.id}`}
                      className="h-9 w-28 rounded-md border hairline surface-card px-2 mono ink text-right text-sm"/>
                  </div>
                  <div className="col-span-2 text-right mono text-sm ink-subtle">
                    {line ? `${line.counted_base} ${i.base_unit}` : "—"}
                  </div>
                  <div className={`col-span-2 text-right mono text-sm ${line ? (Math.abs(line.variance_base) < 0.001 ? "ink" : line.variance_base < 0 ? "text-red-500" : "text-emerald-500") : "ink-subtle"}`}>
                    {line ? `${line.variance_base > 0 ? "+" : ""}${line.variance_base}` : "—"}
                  </div>
                  <div className="col-span-2">
                    {line && Math.abs(line.variance_base) > 0.001 && (
                      <input
                        placeholder="reason (optional)"
                        defaultValue={line.reason || ""}
                        onBlur={(e) => saveLine(i.id, countInput[i.id], e.target.value)}
                        className="h-8 w-full rounded-md border hairline surface-card px-2 text-xs ink"
                        data-testid={`inv-count-reason-${i.id}`}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
